import React, { FC } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  HStack,
  Stack,
  Text,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/all';
import { FiChevronDown } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { AriksaIcon, EllipsisText, Tag } from 'components/DataDisplay';
import { PrimaryButton } from 'components/DataEntry';
import { ThirdPartyIntegrationIcon } from 'components/Icons';
import { AriksaLogoOnlyIcon } from 'components/Icons/ReactCustomIcons/AriksaLogoOnlyIcon';
import { Tabs } from 'components/Navigation';
import { RemediationCommand } from 'containers/Findings/AlertDrawer/Components/Individual/Remediation/RemediationCommand';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';

interface IRemediationAccordion {
  remediations?: any[];
  allowToggle?: boolean;

  onExecuteRemediation?(remediation: any): void;

  showRemediateButton?: boolean;
  remediateDisclosure: UseDisclosureProps;

  autoRemediate?: boolean;
  setAutoRemediate?(val: boolean);

  cloudProvider?: string;
}

export const RemediationAccordion: FC<IRemediationAccordion> = props => {
  const {
    remediations = [],
    allowToggle = true,
    onExecuteRemediation,
    remediateDisclosure,
    showRemediateButton = true,
    autoRemediate,
    setAutoRemediate,
    cloudProvider = 'AWS',
  } = props;
  const { executeRemediation } = useSelector(selectFindingsOverview);
  const handleExecuteRemediation = (e, remediation) => {
    e.stopPropagation();
    onExecuteRemediation?.(remediation);
    remediateDisclosure?.onOpen?.();
  };

  return (
    <Accordion allowToggle={allowToggle}>
      <Stack spacing={4}>
        {remediations?.map((r, index) => (
          <AccordionItem
            border={'1px solid'}
            borderColor={customTheme.colors.gray['100']}
            borderRadius={5}
            key={index + 'remediation-key'}
          >
            {({ isExpanded }) => (
              <>
                <AccordionButton px={2} outline={'none'}>
                  <HStack
                    justify={'space-between'}
                    w={'full'}
                    align="flex-start"
                  >
                    <Center>
                      <AriksaIcon
                        icon={
                          r?.created_by === 'Ariksa' ? (
                            <AriksaLogoOnlyIcon />
                          ) : (
                            <Box
                              boxSize={6}
                              color={customTheme.colors.green['300']}
                            >
                              <ThirdPartyIntegrationIcon />
                            </Box>
                          )
                        }
                        iconFilled={false}
                        size={'sm'}
                      />
                    </Center>
                    <HStack
                      justify={'space-between'}
                      w={'full'}
                      align="flex-start"
                    >
                      <HStack
                        flex="1"
                        textAlign={'left'}
                        overflow={'hidden'}
                        w="full"
                        color={
                          r.execution_button_flag
                            ? 'primary'
                            : customTheme.colors.gray['300']
                        }
                      >
                        {isExpanded ? (
                          <Text>{r.name}</Text>
                        ) : (
                          <EllipsisText>{r.name}</EllipsisText>
                        )}
                        {r?.created_by !== 'Ariksa' && (
                          <Tag
                            label="Custom"
                            styles={{
                              label: { color: 'white' },
                              tag: { bg: 'primary' },
                            }}
                          />
                        )}
                      </HStack>

                      {showRemediateButton && (
                        <PrimaryButton
                          size={'xs'}
                          isDisabled={!r.execution_button_flag}
                          onClick={e => handleExecuteRemediation(e, r)}
                          isLoading={executeRemediation.isLoading}
                        >
                          REMEDIATE
                        </PrimaryButton>
                      )}
                    </HStack>
                    {allowToggle && (
                      <Center pt={1.5}>
                        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                      </Center>
                    )}
                  </HStack>
                </AccordionButton>
                {allowToggle && (
                  <AccordionPanel mt={2} px={4}>
                    <Box py={4} px={1} bg={'gray.50'} borderRadius={6}>
                      <Tabs
                        items={[
                          ...(showRemediateButton
                            ? []
                            : [
                                {
                                  title: 'Ariksa',
                                  key: 'Ariksa',
                                  component: (
                                    <RemediationCommand
                                      type={'Command'}
                                      command={r.cli_commands?.command ?? '-'}
                                      onClickAutoRemediation={
                                        onExecuteRemediation
                                      }
                                      remediation={r}
                                      autoRemediate={autoRemediate}
                                      setAutoRemediate={setAutoRemediate}
                                    />
                                  ),
                                },
                              ]),
                          ...(r.cli_commands
                            ? [
                                {
                                  title: cloudProvider + ' CLI',
                                  key: 'cli',
                                  component: (
                                    <RemediationCommand
                                      type={
                                        'Steps for ' + cloudProvider + ' CLI'
                                      }
                                      steps={
                                        r.cli_commands?.cli_setup_steps ?? []
                                      }
                                      aws_console_steps={
                                        r.console_commands?.aws_console_steps ??
                                        []
                                      }
                                      command={r.cli_commands?.command ?? '-'}
                                      document={r.cli_commands?.document ?? ''}
                                      note={r.cli_commands?.note ?? ''}
                                    />
                                  ),
                                },
                              ]
                            : []),
                          ...(r.console_commands
                            ? [
                                {
                                  title: cloudProvider + ' Console',
                                  key: 'console',
                                  component: (
                                    <RemediationCommand
                                      type={
                                        'Steps for ' +
                                        cloudProvider +
                                        ' console'
                                      }
                                      steps={r.console_commands.steps ?? []}
                                      console_path={
                                        r.console_commands?.console_path ?? ''
                                      }
                                      aws_console_steps={
                                        r.console_commands?.aws_console_steps ??
                                        []
                                      }
                                      note={r.console_commands?.note ?? ''}
                                    />
                                  ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Box>
                  </AccordionPanel>
                )}
              </>
            )}
          </AccordionItem>
        ))}
      </Stack>
    </Accordion>
  );
};
